import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Loader2,
  AlertCircle,
  Package,
  Star,
  DollarSign,
  Hash,
  ShoppingCart,
  Calendar,
  Check,
  X
} from 'lucide-react';
import { Product, FairVendorProduct, FairVendorProductCreate, FairVendorProductUpdate } from '../types';
import { vendorApi } from '../services/api';
import { useVendor } from '../contexts/VendorContext';
import SimplePagination from './SimplePagination';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

interface EditModalProps {
  product: Product;
  fairProduct: FairVendorProduct | null;
  onSave: (data: FairVendorProductCreate | FairVendorProductUpdate) => Promise<void>;
  onClose: () => void;
  isLoading: boolean;
}

interface DeleteConfirmationModalProps {
  product: Product;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
}

function EditFairProductModal({ product, fairProduct, onSave, onClose, isLoading }: EditModalProps) {
  const [fairSpecificPrice, setFairSpecificPrice] = useState(fairProduct?.fair_specific_price || product.base_price || '');
  const [quantityAvailable, setQuantityAvailable] = useState<number | undefined>(fairProduct?.quantity_available);
  const [isFeatured, setIsFeatured] = useState(fairProduct?.is_featured || false);
  const [isAvailableForPreorder, setIsAvailableForPreorder] = useState(fairProduct?.is_available_for_preorder || false);
  const [priceError, setPriceError] = useState<string | null>(null);
  const [quantityError, setQuantityError] = useState<string | null>(null);

  // Feature flag for pre-orders (Issue #1468)
  const { isEnabled: preOrdersEnabled } = useFeatureFlag('preorders_enabled');

  const validatePrice = (value: string): boolean => {
    if (!value) {
      setPriceError(null);
      return true; // Empty is valid (uses base price)
    }

    // Check format: must be valid decimal with up to 2 decimal places
    const priceRegex = /^\d+(\.\d{1,2})?$/;
    if (!priceRegex.test(value)) {
      setPriceError('Price must be a valid number with up to 2 decimal places');
      return false;
    }

    // Check range: must be >= 0 and <= 999999.99
    const numValue = parseFloat(value);
    if (numValue < 0) {
      setPriceError('Price cannot be negative');
      return false;
    }
    if (numValue > 999999.99) {
      setPriceError('Price cannot exceed $999,999.99');
      return false;
    }

    setPriceError(null);
    return true;
  };

  const validateQuantity = (value: number | undefined): boolean => {
    if (value === undefined || value === null) {
      setQuantityError(null);
      return true; // Empty is valid (unlimited)
    }

    if (value < 0) {
      setQuantityError('Quantity cannot be negative');
      return false;
    }

    if (!Number.isInteger(value)) {
      setQuantityError('Quantity must be a whole number');
      return false;
    }

    if (value > 999999) {
      setQuantityError('Quantity cannot exceed 999,999');
      return false;
    }

    setQuantityError(null);
    return true;
  };

  const handlePriceChange = (value: string) => {
    setFairSpecificPrice(value);
    validatePrice(value);
  };

  const handleQuantityChange = (value: string) => {
    const numValue = value ? parseInt(value) : undefined;
    setQuantityAvailable(numValue);
    validateQuantity(numValue);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate before submit
    const isPriceValid = validatePrice(fairSpecificPrice);
    const isQuantityValid = validateQuantity(quantityAvailable);

    if (!isPriceValid || !isQuantityValid) {
      return; // Don't submit if validation fails
    }

    const data = {
      product_id: product.id,
      fair_specific_price: fairSpecificPrice || undefined,
      quantity_available: quantityAvailable,
      is_featured: isFeatured,
      is_available_for_preorder: isAvailableForPreorder && product.is_made_to_order
    };

    await onSave(data);
  };

  // Handle escape key to close modal
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isLoading, onClose]);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 id="modal-title" className="text-xl font-bold text-gray-900">
              {fairProduct ? 'Edit' : 'Add'} Product to Fair
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={isLoading}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Product Info */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                {product.emoji && <span className="text-3xl">{product.emoji}</span>}
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {product.name}
                    {product.variety && <span className="text-gray-600"> - {product.variety}</span>}
                  </h3>
                  <p className="text-sm text-gray-600">
                    Base Price: ${product.base_price || 'Not set'}
                    {product.is_made_to_order && ' • Made to Order'}
                  </p>
                </div>
              </div>
            </div>

            {/* Fair-Specific Price */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <DollarSign className="inline w-4 h-4 mr-1" />
                Fair-Specific Price (optional)
              </label>
              <input
                type="text"
                value={fairSpecificPrice}
                onChange={(e) => handlePriceChange(e.target.value)}
                placeholder={`Default: $${product.base_price || '0.00'}`}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                  priceError ? 'border-red-500' : 'border-gray-300'
                }`}
                aria-invalid={!!priceError}
                aria-describedby={priceError ? 'price-error' : undefined}
              />
              {priceError ? (
                <p id="price-error" className="text-xs text-red-600 mt-1">
                  {priceError}
                </p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty to use base price. Override for fair-specific pricing.
                </p>
              )}
            </div>

            {/* Quantity Available */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Hash className="inline w-4 h-4 mr-1" />
                Quantity Available (optional)
              </label>
              <input
                type="number"
                min="0"
                max="999999"
                step="1"
                value={quantityAvailable || ''}
                onChange={(e) => handleQuantityChange(e.target.value)}
                placeholder="Unlimited if not set"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                  quantityError ? 'border-red-500' : 'border-gray-300'
                }`}
                aria-invalid={!!quantityError}
                aria-describedby={quantityError ? 'quantity-error' : undefined}
              />
              {quantityError ? (
                <p id="quantity-error" className="text-xs text-red-600 mt-1">
                  {quantityError}
                </p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  Set a specific quantity available for this fair, or leave empty for unlimited.
                </p>
              )}
            </div>

            {/* Featured */}
            <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isFeatured}
                  onChange={(e) => setIsFeatured(e.target.checked)}
                  className="w-4 h-4 text-secondary border-gray-300 rounded focus:ring-primary"
                />
                <Star className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-medium text-gray-700">
                  Feature this product at this fair
                </span>
              </label>
              <p className="text-xs text-gray-500 ml-6 mt-1">
                Featured products are highlighted to customers.
              </p>
            </div>

            {/* Pre-order (only for made-to-order products, gated by feature flag Issue #1468) */}
            {preOrdersEnabled && product.is_made_to_order && (
              <div className="mb-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isAvailableForPreorder}
                    onChange={(e) => setIsAvailableForPreorder(e.target.checked)}
                    className="w-4 h-4 text-secondary border-gray-300 rounded focus:ring-primary"
                  />
                  <ShoppingCart className="w-4 h-4 text-secondary" />
                  <span className="text-sm font-medium text-gray-700">
                    Enable pre-orders for this fair
                  </span>
                </label>
                <p className="text-xs text-gray-500 ml-6 mt-1">
                  Allow customers to pre-order this made-to-order product.
                  {product.production_time_days && ` Production time: ${product.production_time_days} days.`}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    {fairProduct ? 'Update' : 'Add to Fair'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function DeleteConfirmationModal({ product, onConfirm, onCancel, isLoading }: DeleteConfirmationModalProps) {
  const productName = product.name + (product.variety ? ` - ${product.variety}` : '');

  // Handle escape key to close modal
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isLoading, onCancel]);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-modal-title"
      aria-describedby="delete-modal-description"
    >
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <h2 id="delete-modal-title" className="text-lg font-semibold text-gray-900">
            Remove Product from Fair?
          </h2>
        </div>

        <p id="delete-modal-description" className="text-gray-700 mb-6">
          Are you sure you want to remove <strong>{productName}</strong> from this fair's catalog?
          This action cannot be undone.
        </p>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Removing...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Remove Product
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function FairProductCatalogManager() {
  const { vendors, reservations, loadVendors, loadReservations } = useVendor();

  // State
  const [selectedVendorId, setSelectedVendorId] = useState<number | null>(null);
  const [selectedFairId, setSelectedFairId] = useState<number | null>(null);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [fairProducts, setFairProducts] = useState<FairVendorProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Product | null>(null);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [announcement, setAnnouncement] = useState<string>('');

  // Pagination for vendor's all products
  const [allProductsPage, setAllProductsPage] = useState(1);
  const [allProductsTotalPages, setAllProductsTotalPages] = useState(1);
  const allProductsPageSize = 20;

  // Pagination for fair products
  const [fairProductsPage, setFairProductsPage] = useState(1);
  const [fairProductsTotalPages, setFairProductsTotalPages] = useState(1);
  const fairProductsPageSize = 20;

  // Enhancement 1: Per-product loading states
  const [loadingProducts, setLoadingProducts] = useState<Set<number>>(new Set());

  // Enhancement 2: Retry configuration
  const MAX_RETRIES = 3;
  const RETRY_DELAY_MS = 1000;

  // Enhancement 3: Debouncing - track operation timestamps to prevent duplicates
  const pendingOperations = useRef<Map<string, number>>(new Map());
  const DEBOUNCE_DELAY_MS = 300;

  // Helper: Retry logic with exponential backoff
  const retryWithBackoff = async <T,>(
    operation: () => Promise<T>,
    retries = MAX_RETRIES,
    delay = RETRY_DELAY_MS
  ): Promise<T> => {
    try {
      return await operation();
    } catch (error: unknown) {
      if (retries === 0) {
        throw error;
      }

      // Determine if we should retry based on error type
      let shouldRetry = false;

      if (error && typeof error === 'object' && 'response' in error) {
        // HTTP error with response - only retry server errors (5xx)
        const response = error as { response?: { status?: number } };
        const status = response.response?.status;
        shouldRetry = status !== undefined && status >= 500;
      } else {
        // Network error (no response) - safe to retry
        shouldRetry = true;
      }

      if (!shouldRetry) {
        throw error;
      }

      // Wait with exponential backoff
      await new Promise(resolve => setTimeout(resolve, delay));

      // Retry with increased delay
      return retryWithBackoff(operation, retries - 1, delay * 2);
    }
  };

  // Helper: Mark product as loading
  const setProductLoading = (productId: number, isLoading: boolean) => {
    setLoadingProducts(prev => {
      const next = new Set(prev);
      if (isLoading) {
        next.add(productId);
      } else {
        next.delete(productId);
      }
      return next;
    });
  };

  // Helper: Check if operation should be debounced
  const shouldDebounce = (key: string): boolean => {
    const lastOperation = pendingOperations.current.get(key);
    if (lastOperation) {
      const timeSinceLastOp = Date.now() - lastOperation;
      if (timeSinceLastOp < DEBOUNCE_DELAY_MS) {
        return true; // Too soon, should debounce
      }
    }
    // Update timestamp for this operation
    pendingOperations.current.set(key, Date.now());
    return false;
  };

  // Helper: Filter products by search term (extracted to avoid duplication)
  const filterProductsBySearch = useCallback((
    product: { name: string; variety?: string | null },
    search: string
  ): boolean => {
    return search === '' ||
      product.name.toLowerCase().includes(search.toLowerCase()) ||
      (product.variety && product.variety.toLowerCase().includes(search.toLowerCase()));
  }, []);

  // Load vendors on mount
  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  // Select first vendor by default
  useEffect(() => {
    if (vendors.length > 0 && !selectedVendorId) {
      setSelectedVendorId(vendors[0].id);
    }
  }, [vendors, selectedVendorId]);

  // Load reservations when vendor changes
  useEffect(() => {
    if (selectedVendorId) {
      loadReservations(selectedVendorId);
    }
  }, [selectedVendorId, loadReservations]);

  // Select first confirmed reservation by default
  useEffect(() => {
    if (reservations.length > 0 && !selectedFairId) {
      const confirmedRes = reservations.find(r => r.status === 'confirmed');
      if (confirmedRes) {
        setSelectedFairId(confirmedRes.fair_id);
      }
    }
  }, [reservations, selectedFairId]);

  // Load vendor's all products
  const loadAllProducts = useCallback(async () => {
    if (!selectedVendorId) return;

    try {
      setLoading(true);
      const response = await vendorApi.getVendorProducts(selectedVendorId, allProductsPage, allProductsPageSize);
      setAllProducts(response.items);
      setAllProductsTotalPages(response.total_pages);
    } catch (err: unknown) {
      console.error('Failed to load products:', err);
      const errorMessage = err && typeof err === 'object' && 'response' in err
        ? (err.response as { data?: { detail?: string } })?.data?.detail || 'Failed to load products'
        : 'Failed to load products';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [selectedVendorId, allProductsPage, allProductsPageSize]);

  // Load fair products
  const loadFairProducts = useCallback(async () => {
    if (!selectedVendorId || !selectedFairId) return;

    try {
      setLoading(true);
      const response = await vendorApi.getVendorProductsForFair(
        selectedVendorId,
        selectedFairId,
        fairProductsPage,
        fairProductsPageSize
      );
      setFairProducts(response.items);
      setFairProductsTotalPages(response.total_pages);
    } catch (err: unknown) {
      console.error('Failed to load fair products:', err);
      const errorMessage = err && typeof err === 'object' && 'response' in err
        ? (err.response as { data?: { detail?: string } })?.data?.detail || 'Failed to load fair products'
        : 'Failed to load fair products';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [selectedVendorId, selectedFairId, fairProductsPage, fairProductsPageSize]);

  // Load data when dependencies change
  useEffect(() => {
    if (selectedVendorId) {
      loadAllProducts();
    }
  }, [loadAllProducts, selectedVendorId]);

  useEffect(() => {
    if (selectedVendorId && selectedFairId) {
      loadFairProducts();
    }
  }, [loadFairProducts, selectedVendorId, selectedFairId]);

  const confirmedReservations = reservations.filter(r => r.status === 'confirmed');

  const handleAddOrEditProduct = (product: Product) => {
    setSelectedProduct(product);
    setShowEditModal(true);
  };

  const handleSaveProduct = async (data: FairVendorProductCreate | FairVendorProductUpdate) => {
    if (!selectedVendorId || !selectedFairId || !selectedProduct) return;

    const fairProduct = fairProducts.find(fp => fp.product_id === selectedProduct.id);
    const productName = selectedProduct.name + (selectedProduct.variety ? ` - ${selectedProduct.variety}` : '');
    const productId = selectedProduct.id;

    // Enhancement 3: Debouncing - Check if operation is too soon after previous
    const operationKey = `save-${productId}`;
    if (shouldDebounce(operationKey)) {
      console.log(`Debouncing save operation for product ${productId}`);
      return; // Skip this operation, too soon after last one
    }

    // Save previous state for rollback
    const previousFairProducts = [...fairProducts];

    try {
      setIsActionLoading(true);
      setError(null);

      // Enhancement 1: Per-product loading state
      setProductLoading(productId, true);

      if (fairProduct) {
        // OPTIMISTIC UPDATE: Update existing product immediately
        const optimisticUpdate: FairVendorProduct = {
          ...fairProduct,
          fair_specific_price: data.fair_specific_price !== undefined ? data.fair_specific_price : fairProduct.fair_specific_price,
          quantity_available: data.quantity_available !== undefined ? data.quantity_available : fairProduct.quantity_available,
          is_featured: data.is_featured !== undefined ? data.is_featured : fairProduct.is_featured,
          is_available_for_preorder: data.is_available_for_preorder !== undefined ? data.is_available_for_preorder : fairProduct.is_available_for_preorder,
        };

        setFairProducts(prev => prev.map(fp => fp.id === fairProduct.id ? optimisticUpdate : fp));
        setShowEditModal(false);
        setSelectedProduct(null);
        setAnnouncement(`${productName} updated in fair catalog`);

        // Enhancement 2: Make API call with retry logic
        await retryWithBackoff(async () => {
          return await vendorApi.updateFairProductDetails(
            selectedVendorId,
            selectedFairId,
            selectedProduct.id,
            data as FairVendorProductUpdate
          );
        });

        // Reload to get server state (await to ensure consistency)
        await loadFairProducts();
      } else {
        // OPTIMISTIC ADD: Add product with temporary ID immediately
        const tempId = -Date.now(); // Negative temp ID to avoid conflicts
        const optimisticProduct: FairVendorProduct = {
          id: tempId,
          vendor_id: selectedVendorId,
          fair_id: selectedFairId,
          product_id: selectedProduct.id,
          fair_specific_price: data.fair_specific_price !== undefined ? data.fair_specific_price : null,
          quantity_available: data.quantity_available !== undefined ? data.quantity_available : 0,
          is_featured: data.is_featured !== undefined ? data.is_featured : false,
          is_available_for_preorder: data.is_available_for_preorder !== undefined ? data.is_available_for_preorder : false,
          product: selectedProduct,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        setFairProducts(prev => [optimisticProduct, ...prev]);
        setShowEditModal(false);
        setSelectedProduct(null);
        setAnnouncement(`${productName} added to fair catalog`);

        // Enhancement 2: Make API call with retry logic
        await retryWithBackoff(async () => {
          return await vendorApi.addProductToFairCatalog(
            selectedVendorId,
            selectedFairId,
            data as FairVendorProductCreate
          );
        });

        // Reload to get real server data with real ID (await to ensure consistency)
        await loadFairProducts();
      }
    } catch (err: unknown) {
      console.error('Failed to save product:', err);
      const errorMessage = err && typeof err === 'object' && 'response' in err
        ? (err.response as { data?: { detail?: string } })?.data?.detail || 'Failed to save product'
        : 'Failed to save product';

      // ROLLBACK: Restore previous state on error
      setFairProducts(previousFairProducts);
      setError(errorMessage);
      setAnnouncement('Failed to save product. Please try again.');
    } finally {
      setIsActionLoading(false);
      // Enhancement 1: Clear per-product loading state
      setProductLoading(productId, false);
    }
  };

  const handleRemoveProduct = async (productId: number) => {
    if (!selectedVendorId || !selectedFairId) return;

    const product = fairProducts.find(fp => fp.product_id === productId)?.product;
    const productName = product ? product.name + (product.variety ? ` - ${product.variety}` : '') : 'Product';

    // Enhancement 3: Debouncing - Check if operation is too soon after previous
    const operationKey = `remove-${productId}`;
    if (shouldDebounce(operationKey)) {
      console.log(`Debouncing remove operation for product ${productId}`);
      return; // Skip this operation, too soon after last one
    }

    // Save previous state for rollback
    const previousFairProducts = [...fairProducts];

    try {
      setIsActionLoading(true);
      setError(null);

      // Enhancement 1: Per-product loading state
      setProductLoading(productId, true);

      // OPTIMISTIC REMOVE: Remove from display immediately
      setFairProducts(prev => prev.filter(fp => fp.product_id !== productId));
      setAnnouncement(`${productName} removed from fair catalog`);

      // Enhancement 2: Make API call with retry logic
      await retryWithBackoff(async () => {
        return await vendorApi.removeProductFromFairCatalog(selectedVendorId, selectedFairId, productId);
      });

      // Reload to confirm server state (await to ensure consistency)
      await loadFairProducts();
    } catch (err: unknown) {
      console.error('Failed to remove product:', err);
      const errorMessage = err && typeof err === 'object' && 'response' in err
        ? (err.response as { data?: { detail?: string } })?.data?.detail || 'Failed to remove product'
        : 'Failed to remove product';

      // ROLLBACK: Restore previous state on error
      setFairProducts(previousFairProducts);
      setError(errorMessage);
      setAnnouncement('Failed to remove product. Please try again.');
    } finally {
      setIsActionLoading(false);
      // Enhancement 1: Clear per-product loading state
      setProductLoading(productId, false);
    }
  };

  // Filter products (memoized for performance)
  const filteredAllProducts = useMemo(() =>
    allProducts.filter(product => filterProductsBySearch(product, searchTerm)),
    [allProducts, searchTerm, filterProductsBySearch]
  );

  const filteredFairProducts = useMemo(() =>
    fairProducts.filter(fp => {
      if (!fp.product) return false;
      return filterProductsBySearch(fp.product, searchTerm);
    }),
    [fairProducts, searchTerm, filterProductsBySearch]
  );

  // Check if product is already in fair catalog
  const isProductInFair = (productId: number) => {
    return fairProducts.some(fp => fp.product_id === productId);
  };

  if (vendors.length === 0 && !loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Vendor Profile</h3>
          <p className="text-gray-600 mb-4">
            You need to create a vendor profile before managing fair products.
          </p>
          <Link
            to="/vendor/profile/new"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
          >
            <Plus className="w-4 h-4" />
            Create Vendor Profile
          </Link>
        </div>
      </div>
    );
  }

  if (confirmedReservations.length === 0 && !loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-moonbone border border-teal-200 rounded-lg p-6 text-center">
          <Calendar className="w-12 h-12 text-secondary mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Confirmed Fair Reservations</h3>
          <p className="text-gray-600 mb-4">
            You need to have at least one confirmed fair reservation before managing products for fairs.
          </p>
          <Link
            to="/vendor/dashboard"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
          >
            <Calendar className="w-4 h-4" />
            View Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Screen reader announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement}
      </div>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2 mb-2">
          <Package className="w-6 h-6" />
          Fair Product Catalog Manager
        </h1>
        <p className="text-gray-600">
          Select products to bring to specific fairs and configure fair-specific pricing and inventory.
        </p>
      </div>

      {/* Fair Selection */}
      {confirmedReservations.length > 0 && (
        <div className="mb-6 p-4 bg-white border border-gray-200 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Calendar className="inline w-4 h-4 mr-1" />
            Select Fair
          </label>
          <select
            value={selectedFairId || ''}
            onChange={(e) => {
              setSelectedFairId(Number(e.target.value));
              setFairProductsPage(1);
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">Choose a fair...</option>
            {confirmedReservations.map((reservation) => (
              <option key={reservation.id} value={reservation.fair_id}>
                {reservation.fair_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-800">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {selectedFairId && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Products in Fair Catalog */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Products in Fair Catalog ({filteredFairProducts.length})
              </h2>
            </div>

            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search fair products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {loading && fairProducts.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-secondary" />
              </div>
            ) : filteredFairProducts.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>No products in this fair's catalog yet.</p>
                <p className="text-sm">Add products from your inventory on the right.</p>
              </div>
            ) : (
              <>
                <div className="space-y-3 mb-4">
                  {filteredFairProducts.map((fairProduct) => {
                    const product = fairProduct.product;
                    if (!product) return null;

                    // Enhancement 1: Check if this product is loading
                    const isProductLoading = loadingProducts.has(product.id);

                    return (
                      <div
                        key={fairProduct.id}
                        className={`p-4 border border-gray-200 rounded-lg hover:border-teal-300 transition-colors relative ${isProductLoading ? 'opacity-60' : ''}`}
                      >
                        {/* Enhancement 1: Loading overlay */}
                        {isProductLoading && (
                          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 rounded-lg z-10">
                            <Loader2 className="w-6 h-6 animate-spin text-secondary" />
                          </div>
                        )}

                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1">
                            {product.emoji && <span className="text-2xl">{product.emoji}</span>}
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-gray-900">
                                {product.name}
                                {product.variety && <span className="text-gray-600"> - {product.variety}</span>}
                              </h3>
                              <div className="flex flex-wrap gap-2 mt-2 text-sm">
                                <span className="text-gray-600">
                                  ${fairProduct.fair_specific_price || product.base_price || '0.00'}
                                </span>
                                {fairProduct.quantity_available !== undefined && fairProduct.quantity_available !== null && (
                                  <span className="text-gray-600">• Qty: {fairProduct.quantity_available}</span>
                                )}
                                {fairProduct.is_featured && (
                                  <span className="inline-flex items-center gap-1 text-amber-600">
                                    <Star className="w-3 h-3 fill-current" />
                                    Featured
                                  </span>
                                )}
                                {fairProduct.is_available_for_preorder && (
                                  <span className="inline-flex items-center gap-1 text-secondary">
                                    <ShoppingCart className="w-3 h-3" />
                                    Pre-order
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <button
                              onClick={() => handleAddOrEditProduct(product)}
                              className="p-2 text-secondary hover:bg-secondary-light/50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Edit"
                              disabled={isProductLoading}
                              aria-label={`Edit ${product.name}${product.variety ? ` - ${product.variety}` : ''}`}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(product)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Remove from fair"
                              disabled={isProductLoading}
                              aria-label={`Remove ${product.name}${product.variety ? ` - ${product.variety}` : ''} from fair`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                <SimplePagination
                  currentPage={fairProductsPage}
                  totalPages={fairProductsTotalPages}
                  onPageChange={setFairProductsPage}
                  disabled={loading}
                  ariaLabel="Fair products pagination"
                />
              </>
            )}
          </div>

          {/* Right: All Vendor Products */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Your Products ({filteredAllProducts.length})
              </h2>
              <Link
                to={`/vendor/products/new?vendorId=${selectedVendorId}`}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark"
              >
                <Plus className="w-4 h-4" />
                New Product
              </Link>
            </div>

            {loading && allProducts.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-secondary" />
              </div>
            ) : filteredAllProducts.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>No products found.</p>
              </div>
            ) : (
              <>
                <div className="space-y-3 mb-4">
                  {filteredAllProducts.map((product) => {
                    const inFair = isProductInFair(product.id);

                    return (
                      <div
                        key={product.id}
                        className={`p-4 border rounded-lg transition-colors ${
                          inFair
                            ? 'border-teal-300 bg-teal-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1">
                            {product.emoji && <span className="text-2xl">{product.emoji}</span>}
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-gray-900">
                                {product.name}
                                {product.variety && <span className="text-gray-600"> - {product.variety}</span>}
                              </h3>
                              <div className="flex flex-wrap gap-2 mt-1 text-sm text-gray-600">
                                <span>${product.base_price || '0.00'}</span>
                                <span>• {product.category}</span>
                                {product.is_made_to_order && (
                                  <span className="text-secondary">• Made to Order</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleAddOrEditProduct(product)}
                            className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                              inFair
                                ? 'bg-primary text-white hover:bg-primary-dark'
                                : 'border border-primary text-secondary hover:bg-secondary-light/50'
                            }`}
                          >
                            {inFair ? (
                              <>
                                <Check className="w-4 h-4" />
                                In Fair
                              </>
                            ) : (
                              <>
                                <Plus className="w-4 h-4" />
                                Add
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                <SimplePagination
                  currentPage={allProductsPage}
                  totalPages={allProductsTotalPages}
                  onPageChange={setAllProductsPage}
                  disabled={loading}
                  ariaLabel="All products pagination"
                />
              </>
            )}
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedProduct && (
        <EditFairProductModal
          product={selectedProduct}
          fairProduct={fairProducts.find(fp => fp.product_id === selectedProduct.id) || null}
          onSave={handleSaveProduct}
          onClose={() => {
            setShowEditModal(false);
            setSelectedProduct(null);
          }}
          isLoading={isActionLoading}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <DeleteConfirmationModal
          product={deleteConfirm}
          onConfirm={async () => {
            await handleRemoveProduct(deleteConfirm.id);
            setDeleteConfirm(null);
          }}
          onCancel={() => setDeleteConfirm(null)}
          isLoading={isActionLoading}
        />
      )}
    </div>
  );
}
